import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, ArrowRight, Zap, ShieldCheck, BadgeIndianRupee, Clock, Gift, Lock, Heart, Mic } from "lucide-react";
import { Layout } from "@/components/Layout";
import { CategoryGrid } from "@/components/CategoryGrid";
import { Trending } from "@/components/Trending";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";
import { useSpeech } from "@/hooks/useSpeech";
import { Hero3D } from "@/components/Hero3D";
import { usePlatforms } from "@/hooks/usePlatforms";

const Hero = () => {
  const { t, lang } = useLang();
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const { listening, supported, start } = useSpeech(lang);
  const plat = usePlatforms();
  const n = plat ? plat.count : "…";

  const onMic = () => {
    if (!supported) { toast.error(t("voice_unsupported")); return; }
    toast.info(t("voice_listening"));
    const ok = start((text) => setQuery(text));
    if (!ok) toast.error(t("voice_error"));
  };

  const submit = (e) => {
    e.preventDefault();
    const q = query.trim();
    navigate(`/valuate?category=mobile${q ? `&model=${encodeURIComponent(q)}` : ""}`);
  };

  const chips = [
    { icon: Zap, key: "chip_realtime" },
    { icon: ShieldCheck, key: "chip_trusted" },
    { icon: BadgeIndianRupee, key: "chip_bestvalue" },
    { icon: Clock, key: "chip_savetime" },
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-brand-light/60 to-white">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[500px] w-[500px] rounded-full bg-brand/10 blur-3xl" />
      <div className="vlx-container relative grid items-center gap-8 py-10 lg:grid-cols-2 lg:gap-6 lg:py-16">
        <div className="vlx-rise">
          <span className="inline-block rounded-full bg-brand-light px-3.5 py-1.5 font-display text-xs font-bold uppercase tracking-widest text-brand">
            {t("hero_eyebrow")}
          </span>
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-brand-navy sm:text-5xl lg:text-6xl">
            {t("hero_title_1")}<br />
            <span className="text-brand">{t("hero_title_2")}</span>
          </h1>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-600 sm:text-lg">
            {t("hero_sub", { n })}
          </p>

          <div className="mt-6 flex flex-wrap gap-x-5 gap-y-3">
            {chips.map((c) => (
              <div key={c.key} className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand/10 text-brand">
                  <c.icon className="h-4 w-4" />
                </span>
                {t(c.key)}
              </div>
            ))}
          </div>

          <form onSubmit={submit} className="mt-7">
            <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white p-2 shadow-[0_8px_30px_rgba(13,71,161,0.10)] focus-within:border-brand">
              <Search className="ml-3 h-5 w-5 shrink-0 text-slate-400" />
              <input
                data-testid="device-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("search_placeholder")}
                className="min-h-[44px] w-full bg-transparent text-base text-slate-800 outline-none placeholder:text-slate-400"
              />
              <button
                data-testid="voice-search-btn"
                type="button"
                onClick={onMic}
                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-colors duration-200 ${
                  listening ? "animate-pulse bg-red-500 text-white" : "bg-brand-light text-brand hover:bg-brand hover:text-white"
                }`}
                aria-label="Voice search"
                title={t("voice_hint")}
              >
                <Mic className="h-5 w-5" />
              </button>
              <button
                data-testid="hero-search-btn"
                type="submit"
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand text-white transition-colors duration-200 hover:bg-brand-hover"
                aria-label="Search"
              >
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </form>
        </div>

        <div className="vlx-pop relative">
          <Hero3D t={t} count={n} />
        </div>
      </div>
    </section>
  );
};

const HowItWorks = () => {
  const { t } = useLang();
  const steps = [
    { n: "1", t: "step1_t", d: "step1_d" },
    { n: "2", t: "step2_t", d: "step2_d" },
    { n: "3", t: "step3_t", d: "step3_d" },
  ];
  const trust = [
    { icon: Gift, t: "trust_free_t", d: "trust_free_d" },
    { icon: Clock, t: "trust_fast_t", d: "trust_fast_d" },
    { icon: Lock, t: "trust_secure_t", d: "trust_secure_d" },
    { icon: Heart, t: "trust_millions_t", d: "trust_millions_d" },
  ];
  return (
    <section id="how" className="relative scroll-mt-24 overflow-hidden bg-[#F0F4F9] py-14 lg:py-20">
      <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-brand/15 blur-3xl" />
      <div className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-emerald-300/25 blur-3xl" />
      <div className="vlx-container relative">
        <p className="font-display text-sm font-bold uppercase tracking-widest text-brand">{t("how_eyebrow")}</p>
        <h2 className="mt-2 max-w-2xl font-display text-3xl font-bold tracking-tight text-brand-navy sm:text-4xl">
          {t("how_title")}
        </h2>

        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {steps.map((s) => (
            <div key={s.n} className="vlx-glass rounded-2xl p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand font-display text-lg font-bold text-white">
                {s.n}
              </span>
              <h3 className="mt-4 font-display text-xl font-semibold text-brand-navy">{t(s.t)}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{t(s.d)}</p>
            </div>
          ))}
        </div>

        <div className="vlx-glass mt-8 grid grid-cols-2 gap-4 rounded-2xl p-6 lg:grid-cols-4">
          {trust.map((x) => (
            <div key={x.t} className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand">
                <x.icon className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display text-sm font-bold text-brand-navy">{t(x.t)}</p>
                <p className="text-xs text-slate-600">{t(x.d)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  useSeo("Valuelix — Free Resale Price Estimate for Used Electronics in India");
  return (
    <Layout>
      <Hero />
      <Trending />
      <CategoryGrid />
      <HowItWorks />
    </Layout>
  );
}
